import * as React from "react";
import {
    Workflow,
    Building2,
    Phone,
    BarChart3,
    Settings,
    CreditCard,
    Bot,
    UsersRound,
    History,
    Wrench,
    Book,
    MessageSquare,
    Puzzle,
    ClipboardCheck,
    FolderOpen,
    ShieldCheck,
    type LucideIcon,
} from "lucide-react";

import Workflows from "@/pages/workflows";
import Assistants from "@/pages/assistants";
import AssistantConfig from "@/pages/assistants/AssistantConfig";
import MultiAgents from "@/pages/multi-agent";
import MultiAgentWorkflowBuilder from "@/pages/multi-agent/components/MultiAgentWorkflowBuilder";
import { default as PhoneNumbers } from "@/pages/phone-numbers";
import { default as PhoneNumberDetails } from "@/pages/phone-numbers/components/phone-number-details";
import Analytics from "@/pages/analytics";
// import ContactSupport from "@/pages/support"; // Commented out - using other support channels
import SettingsPage from "@/pages/settings";
import Billing from "@/pages/billing";
import TeamManagement from "@/pages/team-management";
import Calls from "@/pages/calls";
import CallDetails from "@/pages/calls/components/call-details"; // Import CallDetails component
import DashboardNotFound from "@/pages/dashboard-not-found"; 
import Files from "@/pages/files";
import { UserProfile } from "../api/auth/auth.types";
import Tools from "@/pages/tools";
import ToolDetails from "@/pages/tools/[id]";
// import SolutionBuilder from "@/pages/solution-builder"; // Import SolutionBuilder component
// import NewSolution from "@/pages/solution-builder/new"; // Import NewSolution component
import KnowledgeBasePage from '@/pages/knowledge-base';
import KnowledgeBaseForm from '@/pages/knowledge-base/components/knowledge-base-form';
import Rubrics from "@/pages/rubrics";
import RubricCreate from "@/pages/rubrics/create";
import RubricEdit from "@/pages/rubrics/edit";
// import Embeds from "@/pages/embeds";
// import EmbedConfig from "@/pages/embeds/embed-config";
import Messaging from "@/pages/messaging";

// Widgets imports
import Widgets from "@/pages/widgets";
import WidgetConfig from "@/pages/widgets/widget-config";

// Platform Team imports (for super_admin and manager roles)
import PlatformTeamPage from "@/pages/platform-team";

interface RouteConfig {
    path: string;
    element: React.ComponentType;
    title: string;
    icon?: LucideIcon;
    description?: string;
    showInSidebar?: boolean;
    sidebarPath?: string; // Optional: different path for sidebar navigation (e.g., 'workflows' instead of 'workflows/*')
    children?: RouteConfig[];
    customComponent?: React.ComponentType;
    permission?: keyof UserProfile['permissions'];
}

export const dashboardRoutes: RouteConfig[] = [
    // Analytics
    {
        path: "analytics",
        element: Analytics,
        title: "Analytics",
        icon: BarChart3,
        description: "View analytics and reports",
        showInSidebar: true,
        permission: "analytics",
    },
    // Rubrics
    {
        path: "rubrics",
        element: Rubrics,
        title: "Rubrics",
        icon: ClipboardCheck,
        description: "Manage analytics rubrics and evaluation criteria",
        showInSidebar: true,
        permission: "rubrics",
        children: [
            {
                path: "",
                element: Rubrics,
                title: "Rubrics List",
            },
            {
                path: "new",
                element: RubricCreate,
                title: "New Rubric",
            },
            {
                path: ":rubricId",
                element: RubricEdit,
                title: "Edit Rubric",
            }
        ]
    },
    // Workflows - using single catch-all route to prevent double rendering
    {
        path: "workflows/*",
        sidebarPath: "workflows", // Sidebar should navigate to 'workflows' not 'workflows/*'
        element: Workflows,
        title: "Workflows",
        icon: Workflow,
        description: "Manage and automate your business processes",
        showInSidebar: true,
        permission: "workflows",
    },
    // Assistants
    {
        path: "assistants",
        element: Assistants,
        title: "Assistants",
        icon: Bot,
        description: "Manage your AI assistants",
        showInSidebar: true,
        permission: "assistant",
        children: [
            {
                path: "",
                element: Assistants,
                title: "Assistants List",
            },
            {
                path: "new",
                element: AssistantConfig,
                title: "New Assistant",
            },
            {
                path: ":assistantId",
                element: AssistantConfig,
                title: "Assistant Config",
            }
        ]
    },
    // Phone Numbers
    {
        path: "phone-numbers",
        element: PhoneNumbers,
        title: "Phone Numbers",
        icon: Phone,
        description: "Manage your phone numbers",
        showInSidebar: true,
        permission: "phone_numbers",
        children: [
            {
                path: "",
                element: PhoneNumbers,
                title: "Phone Numbers List",
            },
            {
                path: ":id",
                element: PhoneNumberDetails,
                title: "Phone Number Details",
            }
        ]
    },
    // Tools
    {
        path: "tools",
        element: Tools,
        title: "Tools",
        icon: Wrench,
        description: "Manage your custom tools",
        showInSidebar: true,
        permission: "tools",
        children: [
            {
                path: "",
                element: Tools,
                title: "Tools List",
            },
            {
                path: "new",
                element: ToolDetails,
                title: "New Tool",
            },
            {
                path: ":id",
                element: ToolDetails,
                title: "Tool Details",
            }
        ]
    },
    // Files
    {
        path: "files",
        element: Files,
        title: "Files",
        icon: FolderOpen,
        description: "Manage your Files",
        showInSidebar: true,
        permission: "files",
    },
    // Knowledge Base
    {
        path: "knowledge-base",
        element: KnowledgeBasePage,
        title: "Knowledge Base",
        icon: Book,
        description: "Manage your knowledge base",
        showInSidebar: true,
        permission: "knowledge_base",
        children: [
            {
                path: "",
                element: KnowledgeBasePage,
                title: "Knowledge Base List",
            },
            {
                path: "new",
                element: KnowledgeBaseForm,
                title: "New Knowledge Base",
            },
            {
                path: ":id",
                element: KnowledgeBaseForm,
                title: "Edit Knowledge Base",
            }
        ]
    },
    // Multi Agent
    {
        path: "multi-agent",
        element: MultiAgents,
        title: "Multi Agent",
        icon: UsersRound,
        showInSidebar: true,
        permission: "multi_agent",
        children: [
            {
                path: "",
                element: MultiAgents,
                title: "MultiAgent List",
            },
            {
                path: "new",
                element: MultiAgentWorkflowBuilder,
                title: "New MultiAgent",
            },
            {
                path: ":id",
                element: MultiAgentWorkflowBuilder,
                title: "Edit MultiAgent",
            }
        ]
    },
    // Messaging
    {
        path: "messaging",
        element: Messaging,
        title: "Messaging",
        icon: MessageSquare,
        description: "Manage WhatsApp and Facebook messaging services",
        showInSidebar: true,
        permission: "messaging",
        children: [
            {
                path: "",
                element: Messaging,
                title: "Messaging",
            }
        ]
    },
    // Widgets
    {
        path: "widgets",
        element: Widgets,
        title: "Widgets",
        icon: Puzzle,
        description: "Create advanced customizable widgets for your assistants",
        showInSidebar: true,
        permission: "widgets",
        children: [
            {
                path: "",
                element: Widgets,
                title: "Widgets List",
            },
            {
                path: "new",
                element: WidgetConfig,
                title: "New Widget",
            },
            {
                path: ":id",
                element: WidgetConfig,
                title: "Edit Widget",
            }
        ]
    },
    // Call Logs
    {
        path: "calls",
        element: Calls,
        title: 'Call Logs',
        icon: History,
        description: "View call logs and recordings",
        showInSidebar: true,
        permission: "calls",
        children: [
            {
                path: ":id",
                element: CallDetails,
                title: "Call Details",
                showInSidebar: false,
            }
        ]
    },
    // Team Management
    {
        path: "team-management",
        element: TeamManagement,
        title: "Team Management",
        icon: Building2,
        description: "Manage your team members",
        showInSidebar: true,
        permission: "team_management",
    },
    // Billing
    {
        path: "billing",
        element: Billing,
        title: "Billing",
        icon: CreditCard,
        description: "Manage your billing and subscription",
        showInSidebar: true,
        permission: "billing",
    },
    // Platform Team (only visible to super_admin and manager roles)
    // Note: Permission check is handled inside the component using canAccessRootAdmin
    {
        path: "platform-team",
        element: PlatformTeamPage,
        title: "Platform Team",
        icon: ShieldCheck,
        description: "Manage platform developers and managers",
        showInSidebar: false, // We'll handle sidebar visibility separately based on role
    },
    // Settings (not in sidebar)
    {
        path: "settings",
        element: SettingsPage,
        title: "Settings",
        icon: Settings,
        description: "Configure your account settings",
        showInSidebar: false,
    },
];

export const supportRoutes: RouteConfig[] = [];

// Contact support page commented out - using other support channels
// {
//     path: "/contact",
//     element: ContactSupport,
//     title: "Contact Support",
//     icon: HelpCircle,
//     description: "Get help and support",
//     showInSidebar: false,
// }

// Helper function to generate flat routes for react-router
interface FlatRoute {
    path: string;
    element: React.ComponentType;
    permission?: keyof UserProfile['permissions'];
}

export function generateFlatRoutes(routes: RouteConfig[]): FlatRoute[] {
    const flatRoutes = routes.flatMap(route => {
        if (route.children) {
            // Only include parent route if it has an element AND no index child
            const hasIndexChild = route.children.some(child => child.path === '');
            const parentRoutes = (route.element && !hasIndexChild) ? [{
                path: route.path,
                element: route.element,
                permission: route.permission
            }] : [];
            
            const childRoutes = route.children.map(child => ({
                path: `${route.path}/${child.path}`,
                element: child.element,
                permission: route.permission
            }));
            
            return [
                ...parentRoutes,
                ...childRoutes,
            ];
        }

        return [{
            path: route.path,
            element: route.element,
            permission: route.permission
        }];
    });

    // For dashboard routes, add a catch-all 404 route with the dashboard-specific NotFound
    if (routes === dashboardRoutes) {
        flatRoutes.push({
            path: "*",
            element: DashboardNotFound,
            permission: undefined
        });
    }

    return flatRoutes;
}

// Helper function to get sidebar items from routes
interface SidebarItem {
    title: string;
    href: string;
    icon: React.ReactNode | null;
    description?: string;
    customComponent?: React.ComponentType;
}

export function getSidebarItems(routes: RouteConfig[]): SidebarItem[] {
    const extractSidebarItems = (route: RouteConfig): SidebarItem[] => {
        const items = [];
        
        if (route.showInSidebar) {
            items.push({
                title: route.title,
                href: route.sidebarPath || route.path, // Use sidebarPath if available, otherwise use path
                icon: route.icon ? <route.icon className="h-4 w-4" /> : null,
                description: route.description,
                customComponent: route.customComponent,
            });
        }

        return items;
    };

    return routes.flatMap(route => extractSidebarItems(route));
}
